import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, User } from 'lucide-react';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { wsService } from '../services/WebSocketService';

interface Message {
  id: string;
  sender: string;
  senderName: string;
  senderType: 'customer' | 'admin';
  message: string;
  createdAt: string;
}

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  isAdmin?: boolean;
}

export function SupportTicketModal({ isOpen, onClose, orderId, isAdmin = false }: SupportTicketModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadCurrentUser();
      fetchMessages();
    }
  }, [isOpen, orderId]);

  // WebSocket listener for real-time message updates
  useEffect(() => {
    if (!isOpen) return;

    const handleNotification = (notification: any) => {
      // Refresh messages when tax order message notification received
      if (notification.type === 'tax_list_order_message' || notification.type === 'tax_list_order_customer_message') {
        console.log('Tax order message received via WebSocket, refreshing messages');
        fetchMessages();
      }
    };

    wsService.on('notification', handleNotification);

    return () => {
      wsService.off('notification', handleNotification);
    };
  }, [isOpen, orderId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadCurrentUser = async () => {
    try {
      const userAttributes = await fetchUserAttributes();
      setCurrentUserEmail(userAttributes.email || '');
    } catch (err) {
      console.error('Error loading user:', err);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const endpoint = isAdmin
        ? `/admin/taxlatesdata-orders/${orderId}/tickets`
        : `/taxlatesdata-orders/${orderId}/tickets`;

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();

      // Flatten all messages from all tickets into single chat
      const allMessages: Message[] = [];
      (data.tickets || []).forEach((ticket: any) => {
        (ticket.messages || []).forEach((msg: any) => {
          allMessages.push(msg);
        });
      });

      // Sort by timestamp
      allMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setMessages(allMessages);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) {
      return;
    }

    try {
      setSending(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;

      // Send message to order chat (pass orderId without ORDER# prefix)
      const endpoint = isAdmin
        ? `/admin/support-tickets/${orderId}/messages`
        : `/support-tickets/${orderId}/messages`;

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const getProfileColor = (email: string) => {
    // Generate consistent color based on email
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500'
    ];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the backdrop itself, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-tdb-primary to-tdb-accent p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <MessageCircle className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">Talk to the Team</h2>
              <p className="text-sm text-white/80">Order #{orderId.slice(0, 12)}...</p>
            </div>
          </div>
          <button
            onClick={handleCloseClick}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            type="button"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
          {loading ? (
            <div className="text-center text-gray-500 py-12">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-semibold">Start the conversation!</p>
              <p className="text-sm mt-1">Send a message below and we'll get back to you</p>
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 max-w-md mx-auto">
                <p className="text-sm text-blue-800 font-semibold">⏰ Response Time: Within 24 hours</p>
                <p className="text-xs text-blue-600 mt-1">Our team will respond to your message as soon as possible</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => {
                const isCustomer = message.senderType === 'customer';
                const isMe = message.sender === currentUserEmail;

                return (
                  <div
                    key={message.id}
                    className={`flex items-start space-x-3 ${isMe ? 'flex-row-reverse space-x-reverse' : ''}`}
                  >
                    {/* Profile Picture */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                      isCustomer ? getProfileColor(message.sender) : 'bg-tdb-accent'
                    }`}>
                      {message.senderType === 'admin' ? 'TL' : getInitials(message.senderName || message.sender)}
                    </div>

                    {/* Message Bubble */}
                    <div className={`flex-1 max-w-[70%]`}>
                      <div className={`rounded-2xl px-4 py-3 ${
                        isMe
                          ? 'bg-tdb-accent text-white'
                          : 'bg-white text-gray-900 shadow-sm'
                      }`}>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-semibold">
                            {message.senderType === 'admin' ? 'TaxLatesData Team' : (message.senderName || message.sender)}
                          </span>
                          <span className={`text-xs ${isMe ? 'text-white/70' : 'text-gray-500'}`}>
                            {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="text-sm whitespace-pre-wrap">{message.message}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input */}
        <div className="border-t border-gray-200 bg-white">
          {!isAdmin && (
            <div className="px-6 pt-4 pb-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-800 font-semibold">⏰ Our team typically responds within 24 hours</p>
              </div>
            </div>
          )}
          <div className="p-6 pt-3">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type your message... (Shift+Enter for new line)"
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent resize-none"
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
                className="px-6 py-3 bg-tdb-accent hover:bg-tdb-accent-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 h-[66px]"
              >
                {sending ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Send</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Press Enter to send, Shift+Enter for new line</p>
          </div>
        </div>
      </div>
    </div>
  );
}
